import { useCallback } from 'react';
import { useSnackbars } from '@customerio/pluma-components/react';
import type { Stripe, StripeCardNumberElement } from '@stripe/stripe-js';
import { billing } from '@/services/billing';
import { IS_STRIPE_TEST_MODE } from '@/services/billing/stripe/elements';
import { sentryCaptureException } from '@/utils/sentry';
import type { CreditCardFormAddress } from '../utils/credit-card-form-submit';
import type { CreditsPurchaseConfig } from '../utils/credits-purchase-config';
import type { CreditsPurchaseTotals } from '../utils/credits-purchase-form-helpers';

// The parts of the confirmed Stripe PaymentIntent result we read. Kept loose so the test-mode stub
// and the real Stripe.js result both satisfy it.
type ConfirmResult = {
  paymentIntent?: { payment_method?: string | { id: string } | null } | null;
  error?: { type?: string; message?: string } | null;
};

export type PurchaseSnackbars = {
  create: (options: {
    id?: string;
    variant: 'default' | 'critical';
    message: string;
    autoCloseTimeout?: number;
  }) => void;
  destroy: (id: string) => void;
};

export type CreditsPurchaseSubmitProps = {
  config: CreditsPurchaseConfig;
  quantity: number;
  totals: CreditsPurchaseTotals;
  useSavedCard: boolean;
  saveCard: boolean;
  address: CreditCardFormAddress;
  name: string;
  stripe: Stripe | null;
  cardNumberElement: StripeCardNumberElement | null;
  /** Persists the billing address on the account before the charge (save-card path only). */
  updateAccountAddress: (address: CreditCardFormAddress) => Promise<void>;
  onProcessingChange?: (isProcessing: boolean) => void;
  onSuccess: (info: { savedCard: boolean }) => void;
  onTrackSucceeded: () => void;
  onTrackFailed: (errorType: string) => void;
};

/**
 * Returns a submit function for the credit-purchase form. Owns the progress/success/error snackbars
 * (via `useSnackbars`) and swallows post-surfaced failures so the void-invoked submit never leaks an
 * unhandled rejection.
 */
export function useCreditsPurchaseSubmit(): (deps: CreditsPurchaseSubmitProps) => Promise<void> {
  const snackbars = useSnackbars();
  return useCallback(
    async (deps: CreditsPurchaseSubmitProps) => {
      try {
        await submitCreditsPurchase(deps, snackbars);
      } catch (error) {
        sentryCaptureException(error instanceof Error ? error : new Error('Credits purchase submit failed'));
      }
    },
    [snackbars],
  );
}

/**
 * Orchestrates a credits purchase: (optionally) save the billing address, create the PaymentIntent,
 * confirm the card, (optionally) save the card as default, then poll until the credits are granted.
 * Surfaces a specific snackbar and fires `onTrackFailed` at each failure point. Exported for testing;
 * the form uses {@link useCreditsPurchaseSubmit}.
 */
export async function submitCreditsPurchase(
  deps: CreditsPurchaseSubmitProps,
  snackbars: PurchaseSnackbars,
): Promise<void> {
  const prefix = deps.config.snackbarPrefix;
  deps.onProcessingChange?.(true);
  snackbars.create({
    variant: 'default',
    id: `${prefix}.purchase.inProgress`,
    message: 'Processing your purchase…',
    autoCloseTimeout: 0,
  });

  try {
    if (deps.saveCard && !(await trySaveAddress(deps, snackbars))) {
      return;
    }

    const intent = await createPaymentIntent(deps, snackbars);
    if (intent == null) {
      return;
    }

    const result = await confirmPayment(deps, intent.client_secret, snackbars);
    if (result == null) {
      return;
    }

    if (deps.saveCard) {
      await saveDefaultPaymentMethod(result, snackbars, prefix);
    }

    await finalizePurchase(deps, intent.invoice_id, snackbars);
  } finally {
    snackbars.destroy(`${prefix}.purchase.inProgress`);
    deps.onProcessingChange?.(false);
  }
}

// Persists the billing address; on failure surfaces a snackbar and returns false to abort the flow.
async function trySaveAddress(deps: CreditsPurchaseSubmitProps, snackbars: PurchaseSnackbars): Promise<boolean> {
  try {
    await deps.updateAccountAddress(deps.address);
    return true;
  } catch (error) {
    sentryCaptureException(error);
    snackbars.create({
      variant: 'critical',
      id: `${deps.config.snackbarPrefix}.updateAddress.error`,
      message: "Your billing address couldn't be updated. Try again.",
    });
    return false;
  }
}

// Creates the Stripe PaymentIntent; on failure tracks + surfaces an error and returns null.
async function createPaymentIntent(
  deps: CreditsPurchaseSubmitProps,
  snackbars: PurchaseSnackbars,
): Promise<{ client_secret: string; invoice_id: string } | null> {
  try {
    return await billing.createCreditsPaymentIntent(
      deps.config.creditType,
      deps.quantity,
      {
        country: deps.address.country,
        state: deps.address.state,
        city: deps.address.city,
        zipCode: deps.address.zipCode,
        street: deps.address.addressLine1,
      },
      deps.useSavedCard,
      deps.saveCard,
    );
  } catch (error) {
    sentryCaptureException(error);
    deps.onTrackFailed('stripe_error');
    snackbars.create({
      variant: 'critical',
      id: `${deps.config.snackbarPrefix}.paymentIntent.error`,
      message: "Your purchase couldn't be created. Try again.",
    });
    return null;
  }
}

// Confirms the card payment and validates the result; on any failure tracks + surfaces and returns null.
async function confirmPayment(
  deps: CreditsPurchaseSubmitProps,
  clientSecret: string,
  snackbars: PurchaseSnackbars,
): Promise<ConfirmResult | null> {
  const prefix = deps.config.snackbarPrefix;

  let result: ConfirmResult;
  try {
    result = await runStripeConfirm(deps, clientSecret);
  } catch (error) {
    sentryCaptureException(error);
    deps.onTrackFailed('stripe_error');
    snackbars.create({
      variant: 'critical',
      id: `${prefix}.cardPayment.error`,
      message: "Your payment couldn't be confirmed. Try again.",
    });
    return null;
  }

  if (result.error != null) {
    const isCardError = result.error.type === 'card_error';
    deps.onTrackFailed(isCardError ? 'card_error' : 'stripe_error');
    snackbars.create({
      variant: 'critical',
      id: `${prefix}.cardPayment.error`,
      message:
        isCardError && result.error.message != null
          ? result.error.message
          : "Your payment couldn't be confirmed. Try again.",
    });
    return null;
  }

  return result;
}

// Calls Stripe's confirmCardPayment (or a stub in test mode). Saved cards omit the payment method so
// Stripe charges the one attached to the intent; new cards pass the entered card + billing details.
async function runStripeConfirm(deps: CreditsPurchaseSubmitProps, clientSecret: string): Promise<ConfirmResult> {
  if (IS_STRIPE_TEST_MODE) {
    return { paymentIntent: { payment_method: 'pm_test_default' } };
  }
  if (deps.stripe == null) {
    throw new Error('Stripe is not ready');
  }
  if (deps.useSavedCard) {
    return deps.stripe.confirmCardPayment(clientSecret);
  }
  if (deps.cardNumberElement == null) {
    throw new Error('Stripe card setup never completed');
  }
  return deps.stripe.confirmCardPayment(clientSecret, {
    payment_method: {
      card: deps.cardNumberElement,
      billing_details: { name: deps.name, address: { postal_code: deps.address.zipCode } },
    },
  });
}

// Best-effort: sets the just-charged card as the account default. A failure doesn't fail the purchase.
async function saveDefaultPaymentMethod(
  result: ConfirmResult,
  snackbars: PurchaseSnackbars,
  prefix: string,
): Promise<void> {
  const pm = result.paymentIntent?.payment_method;
  const paymentMethodId = typeof pm === 'string' ? pm : (pm?.id ?? null);
  if (paymentMethodId == null) {
    sentryCaptureException(new Error('credits_purchase: missing payment_method on confirmed paymentIntent'));
    return;
  }
  try {
    await billing.setDefaultPaymentMethod(paymentMethodId);
  } catch (error) {
    sentryCaptureException(error);
    snackbars.create({
      variant: 'default',
      id: `${prefix}.setDefaultPaymentMethod.error`,
      message:
        "Your purchase succeeded, but we couldn't save your card as the default. You can save it from the billing page.",
    });
  }
}

// Polls until the credits are granted, then reports success; a poll failure surfaces an error.
async function finalizePurchase(
  deps: CreditsPurchaseSubmitProps,
  invoiceId: string,
  snackbars: PurchaseSnackbars,
): Promise<void> {
  const { config } = deps;
  try {
    const granted = await billing.pollForCreditsPurchaseTask.perform(config.creditType, invoiceId);
    // The poll resolves `false`/`undefined` when it was cancelled (the form unmounted mid-poll) rather
    // than confirming a grant. Only report success when it actually observed `granted`, so navigating
    // away or switching accounts during the poll never fires success for an unconfirmed purchase.
    if (granted !== true) {
      return;
    }
    deps.onTrackSucceeded();
    snackbars.create({
      variant: 'default',
      id: `${config.snackbarPrefix}.purchase.success`,
      message: config.formatSuccessMessage(deps.totals.totalPriceCents, deps.totals.totalCredits),
    });
    deps.onSuccess({ savedCard: deps.saveCard });
  } catch (error) {
    deps.onTrackFailed('polling_failed');
    const message =
      error instanceof Error && error.message !== ''
        ? error.message
        : "Your purchase couldn't be processed. Contact support.";
    snackbars.create({ variant: 'critical', id: `${config.snackbarPrefix}.purchase.error`, message });
  }
}
